import {
    Accordion,
    Button,
    ButtonGroup,
    Card,
    Content,
    Disclosure,
    DisclosureHeader,
    DisclosurePanel,
    Div,
    Divider,
    Flex,
    Header,
    Heading,
    type Key,
    Link,
    Modal,
    ModalTrigger,
    Span,
    Switch,
    SwitchField,
    Text,
    Tooltip,
    TooltipTrigger
} from "@hopper-ui/components";
import { InfoIcon, LearningPathIcon, WarningIcon } from "@hopper-ui/icons";
import {
    type ScenarioDefinition,
    type ScenarioGroup,
    type ScenarioWorkspaceScope,
    getBlockingDependency,
    getEnabledScenarios,
    getScenarioWorkspaceScope,
    isScenarioAllowed,
    resolveScenarioState,
    scenarioDefinitions,
    setAllScenarios
} from "@sg-protect/core/mocks";
import { reloadPage } from "@sg-protect/core";
import { Trans } from "@sg-protect/federation-runtime/i18n";
import { useContext, useState } from "react";
import { useShellTranslation } from "../i18n/i18next.ts";
import { WorkspaceContext } from "../WorkspaceContext.tsx";
import { ToolButton } from "./ToolButton.tsx";

const SCENARIOS_LIST_MAX_HEIGHT = "min(45rem, calc(95vh - 260px))";

// Fixed display order for scenario sections, independent of each scenario's position in scenarioDefinitions.
const SCENARIO_GROUP_ORDER: readonly ScenarioGroup[] = [
    "Tenant Setup",
    "Initial Crawl",
    "Workspace",
    "Protect",
    "Migrate",
    "MigrationProjects",
    "MigrationProjects - Plan",
    "Partner",
    "Billing",
    "General"
];

const scenarioGroups = SCENARIO_GROUP_ORDER.map(
    group => [group, scenarioDefinitions.filter(definition => definition.group === group)] as const
).filter(([, definitions]) => definitions.length > 0);

const scenarioGroupKeys = scenarioGroups.map(([group]) => group);

const EXPANDED_GROUPS_STORAGE_KEY = "msw-scenarios-expanded-groups";

function readExpandedGroups(): Set<Key> {
    try {
        const raw = localStorage.getItem(EXPANDED_GROUPS_STORAGE_KEY);

        return raw ? new Set(JSON.parse(raw)) : new Set(scenarioGroupKeys);
    } catch {
        return new Set(scenarioGroupKeys);
    }
}

function writeExpandedGroups(keys: Set<Key>): void {
    try {
        localStorage.setItem(EXPANDED_GROUPS_STORAGE_KEY, JSON.stringify(Array.from(keys)));
    } catch {
        console.warn("[MswScenariosConfigurator] Failed to persist expanded groups to localStorage.");
    }
}

function handleResetAll() {
    setAllScenarios({});
    reloadPage();
}

export function MswScenariosConfigurator() {
    const { t } = useShellTranslation();
    const hasActiveScenario = Object.values(getEnabledScenarios()).some(Boolean);

    return (
        <ModalTrigger>
            <ToolButton label={t("MswScenariosConfigurator.title")}>
                <LearningPathIcon
                    {...(hasActiveScenario && {
                        UNSAFE_color: "#FF6A33" // MSW's color
                    })}
                />
            </ToolButton>
            <MswScenariosModal />
        </ModalTrigger>
    );
}

interface ScopedScenariosNoticeProps {
    scope: ScenarioWorkspaceScope;
    workspaceName: string | undefined;
    enabledCount: number;
}

function ScopedScenariosNotice({ scope, workspaceName, enabledCount }: ScopedScenariosNoticeProps) {
    const { t, i18n } = useShellTranslation();
    const enabledSuffix =
        enabledCount === 0 ? "" : ` ${t("MswScenariosConfigurator.scoped.enabled", { count: enabledCount })}`;

    return (
        <Flex alignItems="flex-start" gap="inline-xs">
            <WarningIcon color="warning" size="sm" flexShrink={0} />
            <Text size="sm" color="neutral-weak">
                {workspaceName ? (
                    <>
                        <Trans
                            i18n={i18n}
                            ns="shell"
                            i18nKey="MswScenariosConfigurator.scoped.workspace"
                            values={{ workspaceName }}
                            components={{ strong: <Span fontWeight="body-sm-semibold" /> }}
                        />
                        {enabledSuffix}
                    </>
                ) : (
                    <>
                        {t("MswScenariosConfigurator.scoped.scope", { scope })}
                        {enabledSuffix}
                    </>
                )}
            </Text>
        </Flex>
    );
}

interface ScenarioGroupPanelProps {
    definitions: readonly ScenarioDefinition[];
    localState: Record<string, boolean>;
    isDirtyKey: (key: string) => boolean;
    onToggle: (key: string) => void;
    onHover: (key: string | null) => void;
}

function ScenarioGroupPanel({ definitions, localState, isDirtyKey, onToggle, onHover }: ScenarioGroupPanelProps) {
    const { t } = useShellTranslation();
    const workspaceContext = useContext(WorkspaceContext);
    const currentWorkspaceId = workspaceContext?.currentWorkspace?.id;

    const isScopeActive = (scope: ScenarioWorkspaceScope) => {
        const scopedWorkspaceId = getScenarioWorkspaceScope(scope);

        return scopedWorkspaceId === undefined || scopedWorkspaceId === currentWorkspaceId;
    };

    const isInScope = (definition: ScenarioDefinition) =>
        !definition.workspaceScope || isScopeActive(definition.workspaceScope);

    const hiddenScopes = Array.from(
        new Set(
            definitions.flatMap(definition =>
                definition.workspaceScope && !isScopeActive(definition.workspaceScope)
                    ? [definition.workspaceScope]
                    : []
            )
        )
    );

    return (
        <Flex direction="column" gap="stack-sm">
            {hiddenScopes.map(scope => (
                <ScopedScenariosNotice
                    key={scope}
                    scope={scope}
                    workspaceName={
                        workspaceContext?.workspaces.find(
                            workspace => workspace.id === getScenarioWorkspaceScope(scope)
                        )?.name
                    }
                    enabledCount={
                        definitions.filter(
                            definition => definition.workspaceScope === scope && localState[definition.key]
                        ).length
                    }
                />
            ))}
            {definitions.filter(isInScope).map(definition => {
                const isAllowed = isScenarioAllowed(definition.key, localState);
                const blockingDependency = getBlockingDependency(definition.key, localState);
                const blockingLabel =
                    blockingDependency && scenarioDefinitions.find(d => d.key === blockingDependency.key)?.label;

                return (
                    <Div
                        key={definition.key}
                        onMouseEnter={() => onHover(definition.key)}
                        onMouseLeave={() => onHover(null)}
                        onFocus={() => onHover(definition.key)}
                        onBlur={() => onHover(null)}
                    >
                        <SwitchField>
                            <Switch
                                isSelected={localState[definition.key] ?? false}
                                isDisabled={!isAllowed}
                                onChange={() => onToggle(definition.key)}
                            >
                                {definition.label}
                                {isDirtyKey(definition.key) ? " *" : ""}
                                <Div display={{ base: "inline-flex", sm: "none" }}>
                                    <TooltipTrigger>
                                        <InfoIcon size="sm" />
                                        <Tooltip>
                                            {definition.description}
                                            {blockingLabel &&
                                                ` ${t("MswScenariosConfigurator.blockedBy", { scenario: blockingLabel })}`}
                                        </Tooltip>
                                    </TooltipTrigger>
                                </Div>
                            </Switch>
                        </SwitchField>
                    </Div>
                );
            })}
        </Flex>
    );
}

function MswScenariosModal() {
    const { t } = useShellTranslation();
    const [snapshot] = useState(() => getEnabledScenarios());
    const [localState, setLocalState] = useState(() => resolveScenarioState(snapshot));
    const [hoveredKey, setHoveredKey] = useState<string | null>(null);
    const [expandedGroups, setExpandedGroups] = useState<Set<Key>>(() => readExpandedGroups());
    const hasActiveScenario = Object.values(snapshot).some(Boolean);
    const hoveredDefinition = scenarioDefinitions.find(({ key }) => key === hoveredKey);

    const isDirtyKey = (key: string) => (localState[key] ?? false) !== (snapshot[key] ?? false);
    const isDirty = scenarioDefinitions.some(({ key }) => isDirtyKey(key));

    const handleToggle = (key: string) => {
        setLocalState(prev => resolveScenarioState({ ...prev, [key]: !prev[key] }));
    };

    const handleSave = () => {
        setAllScenarios(localState);
        reloadPage();
    };

    const handleCancel = (onClose: () => void) => {
        setLocalState(snapshot);
        onClose();
    };

    const handleExpandedChange = (keys: Set<Key>) => {
        setExpandedGroups(keys);
        writeExpandedGroups(keys);
    };

    return (
        <Modal overflow="clip" size="lg">
            {({ close }) => (
                <>
                    <Heading>{t("MswScenariosConfigurator.title")}</Heading>
                    <Header>
                        {hasActiveScenario && (
                            <Link onPress={handleResetAll} marginRight="stack-sm">
                                {t("MswScenariosConfigurator.resetAll")}
                            </Link>
                        )}
                    </Header>
                    <Content display="flex" flexDirection="column" gap="stack-sm">
                        <Text marginBottom="stack-sm">{t("MswScenariosConfigurator.description")}</Text>
                        <Card variant="second-level" overflow="hidden" display="flex" flexDirection="row">
                            <Flex
                                direction="column"
                                overflowY="auto"
                                UNSAFE_maxHeight={SCENARIOS_LIST_MAX_HEIGHT}
                                padding="inset-sm"
                                grow={1}
                            >
                                <Accordion
                                    variant="inline"
                                    allowsMultipleExpanded
                                    expandedKeys={expandedGroups}
                                    onExpandedChange={handleExpandedChange}
                                    height="auto"
                                    overflow="visible"
                                >
                                    {scenarioGroups.map(([group, definitions]) => (
                                        <Disclosure id={group} key={group}>
                                            <DisclosureHeader>{group}</DisclosureHeader>
                                            <DisclosurePanel>
                                                <ScenarioGroupPanel
                                                    definitions={definitions}
                                                    localState={localState}
                                                    isDirtyKey={isDirtyKey}
                                                    onToggle={handleToggle}
                                                    onHover={setHoveredKey}
                                                />
                                            </DisclosurePanel>
                                        </Disclosure>
                                    ))}
                                </Accordion>
                            </Flex>
                            <Div display={{ base: "none", sm: "block" }} UNSAFE_height="100%">
                                <Divider orientation="vertical" UNSAFE_height="100%" />
                            </Div>
                            <Div
                                display={{ base: "none", sm: "block" }}
                                UNSAFE_width="12rem"
                                style={{ flexShrink: 0 }}
                                overflowY="auto"
                                UNSAFE_maxHeight={SCENARIOS_LIST_MAX_HEIGHT}
                                backgroundColor="neutral"
                            >
                                <Flex direction="column" gap="stack-xs" padding="inset-md">
                                    {hoveredDefinition ? (
                                        <>
                                            <Text fontWeight="body-md-semibold">{hoveredDefinition.label}</Text>
                                            <Text size="sm" color="neutral-weak">
                                                {hoveredDefinition.description}
                                            </Text>
                                        </>
                                    ) : (
                                        <Text size="sm" color="neutral-weak">
                                            {t("MswScenariosConfigurator.hoverHint")}
                                        </Text>
                                    )}
                                </Flex>
                            </Div>
                        </Card>
                    </Content>
                    <ButtonGroup>
                        <Button variant="secondary" size="sm" onPress={() => handleCancel(close)}>
                            <Text>{t("MswScenariosConfigurator.cancel")}</Text>
                        </Button>
                        <Button variant="primary" size="sm" onPress={handleSave} isDisabled={!isDirty}>
                            <Text>{t("MswScenariosConfigurator.save")}</Text>
                        </Button>
                    </ButtonGroup>
                </>
            )}
        </Modal>
    );
}
